// components/adminLocalAuthorityManagement/AdminLocalAuthorityManagement.jsx

import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Switch,
    Space,
    Typography,
    message,
    Tabs,
    Descriptions,
    Select,
    Spin
} from 'antd';
import {
    MessageOutlined,
    FileTextOutlined,
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/DBContext'; // Adjust the import path
import { functions } from '../../firebase_setup';
const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const AdminLocalAuthorityManagement = () => {

    const {
        fetchLocalAuthorityUsers,
        fetchPendingLocalAuthorityUsers,
        approveLocalAuthorityUser,
        denyLocalAuthorityUser,
        updateUser,
        sendMessage,
        currentUser,
        fetchUserStatus,
        getUserInfo,
        fetchLocalAuthorityReportData
    } = useAuth();

    // States for local authorities
    const [localAuthorities, setLocalAuthorities] = useState([]);
    const [filteredLocalAuthorities, setFilteredLocalAuthorities] = useState([]);
    const [loadingLocalAuthorities, setLoadingLocalAuthorities] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadingStates, setLoadingStates] = useState({});
    const [loadingAction, setLoadingAction] = useState(false);
    const [form] = Form.useForm();

    // States for modals
    const [selectedLocalAuthority, setSelectedLocalAuthority] = useState(null);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [editingLocalAuthority, setEditingLocalAuthority] = useState(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [recipient, setRecipient] = useState(null);

    // States for pending local authorities
    const [pendingLocalAuthorities, setPendingLocalAuthorities] = useState([]);
    const [filteredPendingLocalAuthorities, setFilteredPendingLocalAuthorities] = useState([]);
    const [pendingSearch, setPendingSearch] = useState('');
    const [currentPendingPage, setCurrentPendingPage] = useState(1);
    const [pageSizePending, setPageSizePending] = useState(10);
    const [loadingPending, setLoadingPending] = useState(true);

    useEffect(() => {
        async function loadLocalAuthorities() {
            try {
                const fetchedLocalAuthorities = await fetchLocalAuthorityUsers();
                const convertedLocalAuthorities = fetchedLocalAuthorities.map(la => ({
                    ...la,
                    createdAt: la.createdAt?.toDate().toString()
                }));
                setLocalAuthorities(convertedLocalAuthorities);
                setFilteredLocalAuthorities(convertedLocalAuthorities);

                // Initialize loading states
                const initialLoadingStates = {};
                convertedLocalAuthorities.forEach(la => {
                    initialLoadingStates[la.id] = false;
                });
                setLoadingStates(initialLoadingStates);
            } catch (error) {
                message.error("Failed to load local authorities.");
            } finally {
                setLoadingLocalAuthorities(false);
            }
        }
        loadLocalAuthorities();
    }, []);

    // Filter local authorities based on searchValue
    useEffect(() => {
        const filtered = localAuthorities.filter(la =>
            Object.keys(la).some(key =>
                la[key] && String(la[key]).toLowerCase().includes(searchValue.toLowerCase())
            )
        );
        setFilteredLocalAuthorities(filtered);
    }, [localAuthorities, searchValue]);

    
    // Columns for approved local authorities
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text, record) => (
                <a onClick={() => viewLocalAuthority(record)}>{text}</a>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
            sorter: (a, b) => a.department.localeCompare(b.department),
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active, record) => (
                <Switch
                    checked={active}
                    onChange={() => toggleLocalAuthorityStatus(record)}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    loading={loadingStates[record.id]}
                />
            ),
            sorter: (a, b) => a.active - b.active,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button icon={<MessageOutlined />} onClick={() => messageLocalAuthority(record)}>
                        Message
                    </Button>
                    <Button icon={<FileTextOutlined />} onClick={() => generateLocalAuthorityReport(record)}>
                        Report
                    </Button>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => editLocalAuthority(record)}>
                        Edit
                    </Button>
                </Space>
            ),
        },
    ];

    // Fetch and set pending local authorities
    useEffect(() => {
        async function loadPendingLocalAuthorities() {
            try {
                const fetchedPendingLocalAuthorities = await fetchPendingLocalAuthorityUsers();
                const convertedPendingLocalAuthorities = fetchedPendingLocalAuthorities.map(la => ({
                    ...la,
                    createdAt: la.createdAt?.toDate().toString()
                }));
                setPendingLocalAuthorities(convertedPendingLocalAuthorities);
                setFilteredPendingLocalAuthorities(convertedPendingLocalAuthorities);
            } catch (error) {
                message.error("Failed to load pending local authorities.");
            } finally {
                setLoadingPending(false);
            }
        }
        loadPendingLocalAuthorities();
    }, []);

    // Filter pending local authorities based on pendingSearch
    useEffect(() => {
        const filtered = pendingLocalAuthorities.filter(la =>
            la.name &&la.name.toLowerCase().includes(pendingSearch.toLowerCase()) ||
            la.email && la.email.toLowerCase().includes(pendingSearch.toLowerCase())
        );
        setFilteredPendingLocalAuthorities(filtered);
    }, [pendingSearch, pendingLocalAuthorities]);

    // Columns for pending local authorities
    const pendingColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text, record) => (
                <a onClick={() => viewLocalAuthority(record)}>{text}</a>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        onClick={() => approveLocalAuthority(record.id)}
                        type="primary"
                        icon={<CheckOutlined />}
                        size="small"
                    >
                        Approve
                    </Button>
                    <Button
                        onClick={() => denyLocalAuthority(record.id)}
                        type="danger"
                        icon={<CloseOutlined />}
                        size="small"
                    >
                        Deny
                    </Button>
                </Space>
            ),
        },
    ];

    // Approve a local authority
    const approveLocalAuthority = async (localAuthId) => {
        Modal.confirm({
            title: 'Are you sure?',
            content: 'Do you want to approve this local authority?',
            okText: 'Yes',
            okType: 'primary',
            cancelText: 'No',
            onOk: async () => {
                setLoadingAction(true);
                try {
                    await approveLocalAuthorityUser(localAuthId);
                    message.success("Local Authority approved successfully");

                    // Remove from pending
                    setPendingLocalAuthorities(prev => prev.filter(la => la.id !== localAuthId));
                    setFilteredPendingLocalAuthorities(prev => prev.filter(la => la.id !== localAuthId));

                    // Fetch the approved local authority and add to the approved list
                    const approvedLocalAuth = await getUserInfo(localAuthId);
                    if (approvedLocalAuth) {
                        approvedLocalAuth.createdAt = approvedLocalAuth.createdAt?.toDate().toString();
                        approvedLocalAuth.active = !approvedLocalAuth.disabled;
                        setLocalAuthorities(prev => [...prev, approvedLocalAuth]);
                        setFilteredLocalAuthorities(prev => [...prev, approvedLocalAuth]);
                    }
                } catch (error) {
                    message.error("Failed to approve local authority: " + error.message);
                } finally {
                    setLoadingAction(false);
                }
            }
        });
    };

    // Deny a local authority
    const denyLocalAuthority = async (localAuthId) => {
        Modal.confirm({
            title: 'Are you sure?',
            content: 'Do you want to deny this local authority?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                setLoadingAction(true);
                try {
                    await denyLocalAuthorityUser(localAuthId);
                    message.success("Local Authority denied successfully");

                    // Remove from pending
                    setPendingLocalAuthorities(prev => prev.filter(la => la.id !== localAuthId));
                    setFilteredPendingLocalAuthorities(prev => prev.filter(la => la.id !== localAuthId));
                } catch (error) {
                    message.error("Failed to deny local authority: " + error.message);
                } finally {
                    setLoadingAction(false);
                }
            }
        });
    };

    const viewLocalAuthority = (localAuth) => {
        setSelectedLocalAuthority(localAuth);
        setIsViewModalVisible(true);
    };

    const handleViewCancel = () => {
        setIsViewModalVisible(false);
    };

    const toggleLocalAuthorityStatus = async (localAuth) => {
        setLoadingStates(prev => ({ ...prev, [localAuth.id]: true }));
        try {
            const newStatus = !localAuth.active;
            const result = await functions.httpsCallable('toggleUserStatus')({
                userId: localAuth.id,
                disable: localAuth.active,
            });
            if (result.data.success) {
                setLocalAuthorities(prevLocalAuths =>
                    prevLocalAuths.map(la =>
                        la.id === localAuth.id ? { ...la, active: newStatus } : la
                    )
                );
                message.success(`Local Authority ${newStatus ? 'activated' : 'deactivated'} successfully.`);
            } else {
                message.error(result.data.message);
            }
        } catch (error) {
            message.error('Failed to update local authority status.');
            console.error('Error toggling local authority status:', error);
        } finally {
            setLoadingStates(prev => ({ ...prev, [localAuth.id]: false }));
        }
    };

    const editLocalAuthority = (localAuth) => {
        setEditingLocalAuthority(localAuth);
        form.setFieldsValue(localAuth);
        setIsEditModalVisible(true);
    };

    const handleEditOk = async () => {
        setLoadingAction(true);
        if (editingLocalAuthority) {
            const updatedData = form.getFieldsValue();
            try {
                await updateUser(editingLocalAuthority.id, updatedData);
                setLocalAuthorities(prevLocalAuths =>
                    prevLocalAuths.map(la =>
                        la.id === editingLocalAuthority.id ? { ...la, ...updatedData } : la
                    )
                );
                message.success('Local Authority details updated successfully.');
                setIsEditModalVisible(false);
                setEditingLocalAuthority(null);
            } catch (error) {
                message.error('Failed to update local authority details.');
                console.error('Error updating local authority:', error);
            } finally {
                setLoadingAction(false);
            }
        }
    };

    const handleEditCancel = () => {
        setIsEditModalVisible(false);
    };

    const messageLocalAuthority = (localAuth) => {
        setRecipient(localAuth);
        setIsMessageModalVisible(true);
    };

    const messageAllLocalAuthorities = () => {
        setRecipient(null);
        setIsMessageModalVisible(true);
    };

    const handleSendMessage = async () => {
        setLoadingAction(true);
        if (recipient) {
            try {
                await sendMessage(currentUser.uid, recipient.name, recipient.id, messageContent);
                message.success(`Message sent to ${recipient.name}`);
            } catch (error) {
                message.error('Failed to send message to the local authority.');
                console.error('Error sending message:', error);
            }
        } else {
            try {
                await Promise.all(
                    localAuthorities.map(localAuth =>
                        sendMessage(currentUser.uid, localAuth.name, localAuth.id, messageContent)
                    )
                );
                message.success('Message sent to all local authorities.');
            } catch (error) {
                message.error('Failed to send message to all local authorities.');
                console.error('Error sending message:', error);
            }
        }
        setIsMessageModalVisible(false);
        setLoadingAction(false);
    };
    const [loadingReport, setLoadingReport] = useState(false);

    const generateLocalAuthorityReport = async (user) => {
        setLoadingReport(true);
        message.info(`Generating report for ${user.name}`);
    
        try {
          // Fetch user report data using the existing function
          const reportData = await fetchLocalAuthorityReportData(user.id);
      
          // Prepare CSV data
          const csvRows = [];
      
          // Add headers for the profile section
          csvRows.push(['User Profile']);
      
          // Add user profile data
          csvRows.push(['First Name', reportData.firstName || '']);
          csvRows.push(['Last Name', reportData.lastName || '']);
    
          csvRows.push(['Email', reportData.email || '']);
          csvRows.push(['Department', reportData.department || '']);
      
          csvRows.push(['Address', reportData.address || '']);

      
          // Convert rows to CSV string
          const csvContent = csvRows.map((e) => e.join(',')).join('\n');
      
          // Create a Blob from the CSV content
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
          // Create a link to download the Blob
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `${user.name}_Report.csv`);
      
          // Append the link to the document body and click it programmatically
          document.body.appendChild(link);
          link.click();
      
          // Clean up by removing the link
          document.body.removeChild(link);
      
          message.success(`Report generated for ${user.name}`);
        } catch (error) {
          message.error(`Failed to generate report for ${user.name}`);
          console.error('Error generating user report:', error);
        } finally {
          setLoadingReport(false);
        }    };

    const generateAllLocalAuthorityReports = async () => {
        message.info(`Generating report for all Local Authorities`);

        setLoadingReport(true);
        try {
          const allUsers = await fetchLocalAuthorityUsers();
      
          // Fetch all users' report data in parallel
          const allReports = await Promise.all(
            allUsers.map(async (user) => {
              try {
                const reportData = await fetchLocalAuthorityReportData(user.id);
                return { user, reportData };
              } catch (error) {
                console.error(`Error fetching report data for user ${user.id}:`, error);
                return null; // Handle individual user fetch errors
              }
            })
          );
      
          const csvRows = [];
      
          allReports.forEach((report) => {
            if (!report) return; // Skip if report data is null due to an error
            const { user, reportData } = report;
      
            // Add a separator for each user
            csvRows.push([]);
            csvRows.push([`User ID: ${user.id}`]);
            csvRows.push([]);
      
            // User Profile
            csvRows.push(['User Profile']);
            csvRows.push(['First Name', reportData.firstName || '']);
            csvRows.push(['Last Name', reportData.lastName || '']);
          
            csvRows.push(['Email', reportData.email || '']);
            csvRows.push(['Department', reportData.department || '']);
        
            csvRows.push(['Address', reportData.address || '']);
  
      
          });
      
          // Convert rows to CSV string
          const csvContent = csvRows.map((e) => e.join(',')).join('\n');
      
          // Create Blob and trigger download
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
      
          // Create a link and trigger download
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `All_LocalAuthority_Report.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      
          message.success('Report generated for all Local Authorities');
        } catch (error) {
          message.error('Failed to generate report for all Local Authorities.');
          console.error('Error generating all Local Authorities report:', error);
        } finally {
          setLoadingReport(false);
        }    };

    const handleLocalAuthorityPageChange = (page, newPageSize) => {
        setCurrentPage(page);
        setPageSize(newPageSize);
    };

    // Loading spinner while data is fetching
    if (loadingLocalAuthorities || loadingPending) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
            <Title level={3}>Local Authority Management</Title>
            <Tabs defaultActiveKey="1" type="card">
                {/* Approved Local Authorities Tab */}
                <TabPane tab="Local Authority" key="1">
                    <Space
                        style={{
                            marginBottom: 16,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >
                        <Input
                            placeholder="Search local authorities"
                            onChange={(e) => setSearchValue(e.target.value)}
                            style={{ height: '38px', lineHeight: '38px', width: '600px' }}
                        />
                        <Button
                            icon={<MessageOutlined />}
                            onClick={messageAllLocalAuthorities}
                        >
                            Message All Local Authorities
                        </Button>
                        <Button
                            icon={<FileTextOutlined />}
                            onClick={generateAllLocalAuthorityReports}
                        >
                            Generate Reports for All
                        </Button>
                    </Space>
                    <Table
                        columns={columns}
                        dataSource={filteredLocalAuthorities}
                        rowKey="id"
                        pagination={{
                            position: ['bottomCenter'],
                            current: currentPage,
                            pageSize: pageSize,
                            total: filteredLocalAuthorities.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '30', '50', '100'],
                            onChange: handleLocalAuthorityPageChange,
                        }}
                        style={{ cursor: 'default' }}
                        scroll={{ x: 'auto' }}
                    />
                </TabPane>

                {/* Pending Local Authorities Tab */}
                <TabPane tab="Pending Local Authority Approvals" key="2">
                    <Space
                        style={{
                            marginBottom: 16,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                        size="middle"
                    >
                        <Input
                            placeholder="Search pending local authorities"
                            onChange={(e) => setPendingSearch(e.target.value)}
                            style={{ width: '600px', height: '38px', marginRight: '8px' }}
                        />
                    </Space>
                    <Table
                        columns={pendingColumns}
                        dataSource={filteredPendingLocalAuthorities}
                        rowKey="id"
                        pagination={{
                            position: ['bottomCenter'],
                            current: currentPendingPage,
                            pageSize: pageSizePending,
                            total: pendingLocalAuthorities.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '30', '50', '100'],
                            onChange: (page, newPageSize) => {
                                setCurrentPendingPage(page);
                                setPageSizePending(newPageSize);
                            },
                            onShowSizeChange: (current, size) => {
                                setPageSizePending(size);
                            }
                        }}
                        style={{ cursor: 'default' }}
                        scroll={{ x: 'auto' }}
                    />
                </TabPane>
            </Tabs>

            {/* Local Authority Details Modal */}
            <Modal
                title="Local Authority Details"
                visible={isViewModalVisible}
                onCancel={handleViewCancel}
                footer={[
                    <Button key="close" onClick={handleViewCancel}>
                        Close
                    </Button>,
                ]}
                width={800}
            >
                {selectedLocalAuthority && (
                    <Descriptions
                        bordered
                        column={1}
                        size="small"
                    >
                        <Descriptions.Item label="Name">
                            {selectedLocalAuthority.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                            {selectedLocalAuthority.email}
                        </Descriptions.Item>
                        <Descriptions.Item label="Department">
                            {selectedLocalAuthority.department}
                        </Descriptions.Item>
                        <Descriptions.Item label="Address">
                            {selectedLocalAuthority.address}
                        </Descriptions.Item>
                        {/* <Descriptions.Item label="Stripe ID">
                            {selectedLocalAuthority.stripeId}
                        </Descriptions.Item>
                        <Descriptions.Item label="Stripe Link">
                            <a href={selectedLocalAuthority.stripeLink} style={{ color: '#1890ff', fontWeight: 'bold' }} target="_blank" rel="noopener noreferrer">
                                {selectedLocalAuthority.stripeLink}
                            </a>
                        </Descriptions.Item> */}
                        <Descriptions.Item label="Created At">
                            {selectedLocalAuthority.createdAt}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            {selectedLocalAuthority.active ? 'Active' : 'Inactive'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Edit Local Authority Modal */}
            <Modal
                title="Edit Local Authority Details"
                visible={isEditModalVisible}
                onOk={handleEditOk}
                onCancel={handleEditCancel}
                okText="Save"
                confirmLoading={loadingAction}
                width={800}
            >
                {editingLocalAuthority && (
                    <Form layout="vertical" form={form} initialValues={editingLocalAuthority}>
                        <Form.Item label="Name" name="name">
                            <Input />
                        </Form.Item>
                        {/* Email is typically not editable */}
                        <Form.Item label="Department" name="department">
                            <Select>
                                <Option value="Department for Work and Pensions">Department for Work and Pensions</Option>
                                <Option value="Department of Health and Social Care">Department of Health and Social Care</Option>
                                {/* Add more departments as needed */}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Address" name="address">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Stripe ID" name="stripeId">
                            <Input disabled />
                        </Form.Item>
                        <Form.Item label="Stripe Link" name="stripeLink">
                            <Input disabled />
                        </Form.Item>
                        {/* Add more fields if necessary */}
                    </Form>
                )}
            </Modal>

            {/* Message Modal */}
            <Modal
                title={recipient ? `Send Message to ${recipient.name}` : 'Send Message to All Local Authorities'}
                visible={isMessageModalVisible}
                onOk={handleSendMessage}
                onCancel={() => setIsMessageModalVisible(false)}
                okText="Send"
                confirmLoading={loadingAction}
            >
                <Form layout="vertical">
                    <Form.Item label="Message">
                        <Input.TextArea
                            rows={4}
                            value={messageContent}
                            onChange={(e) => setMessageContent(e.target.value)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminLocalAuthorityManagement;
